import type { CSSProperties, JSX } from 'react'

import { clsx } from 'clsx'

import type { Image } from '~publish/legacy/composer/composer/entities/factories'
import { getEntriesWithKeys } from '~publish/helpers/getEntriesWithKeys'
import { MediaAttachmentTypes } from '~publish/legacy/constants'

import styles from './ImagesGrid.module.css'

const formatImagesInRows = (
  images: Image[],
): {
  firstRowCol: Image[]
  secondRowCol: Image[]
} => {
  const total = images.length
  let firstRowCol: Image[] = []
  let secondRowCol: Image[] = []

  if (total === 0) {
    return { firstRowCol, secondRowCol }
  }

  if (total <= 3) {
    firstRowCol = images.slice(0, 1)
    secondRowCol = images.slice(1)
  }

  if (total >= 4) {
    firstRowCol = images.slice(0, 2)
    secondRowCol = images.slice(2, Math.min(images.length, 5))
  }

  return { firstRowCol, secondRowCol }
}

const ImagesRowCol = ({
  type,
  images,
  fullSize,
  isRowFormat,
  totalImages,
  shouldShowMore,
  shouldShowGifTag,
  roundedCorners,
  withBlur,
}: {
  type: string
  images: Image[]
  fullSize: boolean
  isRowFormat: boolean
  totalImages: number
  shouldShowMore: boolean
  shouldShowGifTag: boolean
  roundedCorners?: boolean
  withBlur?: boolean
}): JSX.Element => {
  const extraImages = totalImages - 4
  const content = getEntriesWithKeys(
    images,
    (image) => image.source?.id ?? image.url,
  ).map(({ key, item: imageData }, index) => {
    const { url, altText } = imageData
    const lastImageIndex = images.length - 1
    return (
      <div className={styles.imageWrapper} key={key}>
        <img
          className={clsx(styles.image, {
            [styles.imageRounded]: roundedCorners,
            [styles.imageBlur]: withBlur,
          })}
          src={url}
          alt={altText || ''}
        />
        {imageData.mediaType === MediaAttachmentTypes.GIF &&
          shouldShowGifTag && <span className={styles.tag}>GIF</span>}
        {index === lastImageIndex && shouldShowMore && (
          <div className={clsx(styles.showMore, 'show-more')}>
            +{extraImages}
          </div>
        )}
      </div>
    )
  })

  const singleChild = images.length === 1
  if (isRowFormat) {
    return (
      <div className={styles.rowWrapper} key={type}>
        {content}
      </div>
    )
  }

  const totalImagesInCol = images.length
  const colChildHeight = singleChild
    ? '100%'
    : totalImagesInCol === 3
      ? 'calc(33.33% - 2px)'
      : 'calc(50% - 1.5px)'

  const style: CSSProperties & { '--col-child-height': string } = {
    '--col-child-height': colChildHeight,
  }

  return (
    <div
      className={clsx(styles.colWrapper, {
        [styles.colWrapperFullSize]: fullSize,
      })}
      style={style}
      key={type}
    >
      {content}
    </div>
  )
}

const RowsCols = ({
  firstRowCol,
  secondRowCol,
  fullSize,
  isRowFormat,
  totalImages,
  shouldShowGifTag,
  roundedCorners,
  withBlur,
}: {
  firstRowCol: Image[]
  secondRowCol: Image[]
  fullSize: boolean
  isRowFormat: boolean
  totalImages: number
  shouldShowGifTag: boolean
  roundedCorners?: boolean
  withBlur?: boolean
}): JSX.Element => (
  <>
    {firstRowCol?.length > 0 && (
      <ImagesRowCol
        images={firstRowCol}
        type="first-images"
        fullSize={fullSize}
        isRowFormat={isRowFormat}
        totalImages={totalImages}
        shouldShowMore={false}
        shouldShowGifTag={shouldShowGifTag}
        roundedCorners={roundedCorners}
        withBlur={withBlur}
      />
    )}
    {secondRowCol?.length > 0 && (
      <ImagesRowCol
        images={secondRowCol}
        type="second-images"
        fullSize={fullSize}
        isRowFormat={isRowFormat}
        totalImages={totalImages}
        shouldShowMore={totalImages > 5}
        shouldShowGifTag={shouldShowGifTag}
        roundedCorners={roundedCorners}
        withBlur={withBlur}
      />
    )}
  </>
)

export const ImagesGrid = ({
  images,
  isRowFormat = false,
  formatImages = formatImagesInRows,
  maxHeight = '300px',
  shouldShowGifTag = false,
  className,
  roundedCorners,
  withBlur = false,
}: {
  images: Image[]
  isRowFormat?: boolean
  shouldShowGifTag?: boolean
  formatImages?: (images: Image[]) => {
    firstRowCol: Image[]
    secondRowCol: Image[]
  }
  maxHeight?: string
  className?: string
  roundedCorners?: boolean
  withBlur?: boolean
}): JSX.Element => {
  const { firstRowCol, secondRowCol } = formatImages(images)

  const double = firstRowCol?.length > 0 && secondRowCol?.length > 0
  const fullSize = !double

  return (
    <div
      data-testid="preview-images"
      className={clsx(
        styles.wrapper,
        { [styles.wrapperRowLayout]: isRowFormat },
        className,
      )}
      style={{ height: fullSize ? '100%' : maxHeight }}
    >
      <RowsCols
        firstRowCol={firstRowCol}
        secondRowCol={secondRowCol}
        fullSize={fullSize}
        isRowFormat={isRowFormat}
        totalImages={images.length}
        shouldShowGifTag={shouldShowGifTag}
        roundedCorners={roundedCorners}
        withBlur={withBlur}
      />
    </div>
  )
}
